import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Input, UncontrolledTooltip } from 'reactstrap';
import * as userService from '../Services/userService';
import FormValidator from '../../Forms/FormValidator.js';
import { useTranslation } from 'react-i18next';
import { DotLoader } from '../Components/shared/dotLoader.jsx';
import useAuthStore from '../../../stores/authStore.js';

const Login = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [formLogin, setFormLogin] = useState({
    email: '',
    password: '',
    errors: {},
  });
  const [loading, setLoading] = useState(false);

  // Validate input using onChange event
  const validateOnChange = (event) => {
    const input = event.target;
    const value = input.type === 'checkbox' ? input.checked : input.value;
    const result = FormValidator.validate(input);

    setFormLogin((prev) => ({
      ...prev,
      [input.name]: value,
      errors: {
        ...prev.errors,
        [input.name]: result,
      },
    }));
  };

  // Simplify error check
  const hasError = (inputName, method) => {
    return formLogin.errors && formLogin.errors[inputName] && formLogin.errors[inputName][method];
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const inputs = [...form.elements].filter((i) => ['INPUT', 'SELECT'].includes(i.nodeName));
    const { errors, hasError: formHasError } = FormValidator.bulkValidate(inputs);

    if (!formHasError) {
      setFormLogin((prev) => ({
        ...prev,
        errors,
      }));
      setLoading(true);

      try {
        const result = await userService.login({
          username: formLogin.email,
          password: formLogin.password,
        });

        // --- LEGACY LOGIN LOGIC ---
        if (typeof window.dispatch !== 'undefined' && window.dispatch.login) {
          await window.dispatch.login(result.data);
        } else if (typeof global !== 'undefined' && global.dispatch && global.dispatch.login) {
          await global.dispatch.login(result.data);
        }

        // --- ZUSTAND LOGIN LOGIC ---
        const login = useAuthStore.getState().login;
        await login(result.data);

        history.push('/dashboard');
      } catch (ex) {
        // handle error (add your error handling here)
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="block-center mt-4 wd-xl">
      <div className="card card-flat">
        <div className="card-header text-center bg-dark">
          <a href="">
            <img className="block-center rounded" src="img/logo.png" alt="Logo" />
          </a>
        </div>
        <div className="card-body">
          <p className="text-center py-2">{loading ? t('login.SIGNING_IN') : t('login.SIGN_IN_CONTINUE')}</p>
          {loading ? (
            <DotLoader />
          ) : (
            <form className="mb-3" name="formLogin" onSubmit={onSubmit}>
              <div className="form-group">
                <div className="input-group with-focus">
                  <Input
                    type="email"
                    name="email"
                    className="border-right-0"
                    placeholder={t('login.ENTER_EMAIL')}
                    invalid={hasError('email', 'required') || hasError('email', 'email')}
                    onChange={validateOnChange}
                    data-validate='["required", "email"]'
                    value={formLogin.email}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text text-muted bg-transparent border-left-0">
                      <em className="fa fa-envelope"></em>
                    </span>
                  </div>
                  {hasError('email', 'required') && <span className="invalid-feedback">{t('common.REQUIRED')}</span>}
                  {hasError('email', 'email') && <span className="invalid-feedback">{t('settings.EMAIL_ERROR')}</span>}
                </div>
              </div>
              <div className="form-group">
                <div className="input-group with-focus">
                  <Input
                    type="password"
                    id="id-password"
                    name="password"
                    className="border-right-0"
                    placeholder={t('common.PASSWORD')}
                    invalid={hasError('password', 'required')}
                    onChange={validateOnChange}
                    data-validate='["required"]'
                    value={formLogin.password}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text text-muted bg-transparent border-left-0">
                      <em className="fa fa-lock"></em>
                    </span>
                  </div>
                  <span className="invalid-feedback">{t('common.REQUIRED')}</span>
                </div>
              </div>
              <div className="clearfix">
                <div className="float-right">
                  <Link to="recover" className="text-muted">
                    {t('user.FORGOT_PASSWORD')}
                  </Link>
                </div>
              </div>
              <button className="btn btn-block btn-primary mt-3" type="submit">
                {t('login.LOGIN')}
              </button>
            </form>
          )}
          <p className="pt-3 text-center">{t('login.DONT_HAVE_AN_ACCOUNT')}</p>
          {/* TODO: Add a message when hover: "Currently we do not accept new registrations." */}
          <button id="registerTooltip" className="btn btn-block btn-secondary">
            {t('login.REGISTER_NOW')}
          </button>
          <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target="registerTooltip">
            {t('login.NO_REGISTRATIONS')}
          </UncontrolledTooltip>
          {/* Hidden register button */}
          <Link
            to="register"
            className="btn btn-block btn-secondary invisible-register-button"
            style={{ pointerEvents: loading ? 'none' : null }}
          >
            {t('login.REGISTER_NOW')}
          </Link>
        </div>
      </div>
      <div className="p-3 text-center">
        <span className="mr-2">&copy;</span>
        <span>2025</span>
        <span className="mx-2">-</span>
        <span>Talent Home Admin</span>
        <br />
        <span>Admin Early Access - support@talenthome.io</span>
      </div>
    </div>
  );
};

export default Login;
